import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import { Box, ButtonBase, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import AudioPlayButton from '../../student/components/AudioPlayButton';
import ProgressRing from '../../student/components/ProgressRing';
import PronunciationDetail from '../../student/components/exercises/PronunciationDetail';
import { mockFeedbackAttempts, SpeakingAttemptFeedback } from '../../student/mockFeedback';
import { BLUE, cardSx, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SUCCESS_TEXT, SURFACE_SOFT, YELLOW } from '../../student/studentStyles';

type FeedbackFilter = 'all' | 'reviewed' | 'awaiting';

function AttemptListItem({ attempt, selected, onSelect }: { attempt: SpeakingAttemptFeedback; selected: boolean; onSelect: () => void }) {
    const reviewed = attempt.review != null;
    const score = attempt.review?.teacherScore ?? attempt.aiScore;
    return (
        <ButtonBase
            onClick={onSelect}
            aria-pressed={selected}
            sx={{
                width: '100%', p: 2, gap: 1.5, alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left',
                borderRadius: '16px', border: `2px solid ${selected ? BLUE : 'transparent'}`,
                bgcolor: selected ? `${BLUE}08` : '#fff', boxShadow: selected ? 'none' : '0 2px 8px rgba(0,0,0,.06)',
            }}
        >
            <ProgressRing percent={score} size={46} thickness={5} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14, color: NAVY }}>{attempt.sentenceHanzi}</Typography>
                <Typography noWrap sx={{ mt: 0.25, fontFamily: INTER, fontSize: 11.5, color: SLATE }}>{attempt.exerciseLabel}</Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
                    {reviewed ? <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 15 }} /> : <HourglassTopRoundedIcon sx={{ color: NAVY, fontSize: 15 }} />}
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: reviewed ? SUCCESS_TEXT : NAVY }}>{reviewed ? 'Teacher reviewed' : 'Awaiting review'}</Typography>
                </Stack>
            </Box>
            <ChevronRightRoundedIcon sx={{ color: selected ? BLUE : SLATE, fontSize: 20 }} />
        </ButtonBase>
    );
}

function FeedbackDetail({ attempt }: { attempt: SpeakingAttemptFeedback }) {
    const reviewed = attempt.review != null;
    const score = attempt.review?.teacherScore ?? attempt.aiScore;
    return (
        <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-start' }} spacing={2}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE }}>{attempt.exerciseLabel.toUpperCase()}</Typography>
                        <Chip
                            icon={reviewed ? <CheckCircleRoundedIcon /> : <HourglassTopRoundedIcon />}
                            label={reviewed ? 'Reviewed by your teacher' : 'Awaiting teacher review'}
                            size="small"
                            sx={{ height: 24, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: reviewed ? SUCCESS_TEXT : NAVY, bgcolor: reviewed ? `${GREEN}14` : `${YELLOW}3D`, '& .MuiChip-icon': { color: reviewed ? GREEN : NAVY, fontSize: 16 } }}
                        />
                    </Stack>
                    <Typography component="h2" sx={{ mt: 1.5, fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 23, md: 28 }, color: NAVY, lineHeight: 1.3 }}>{attempt.sentenceHanzi}</Typography>
                    <Typography sx={{ mt: 0.3, fontFamily: INTER, fontSize: 13, color: BLUE }}>{attempt.sentencePinyin}</Typography>
                    <Typography sx={{ mt: 0.5, fontFamily: INTER, fontSize: 12.5, color: SLATE }}>{attempt.lessonName} · Submitted {attempt.submitted}</Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AudioPlayButton ariaLabel="Replay your recording" size={42} />
                    <ProgressRing percent={score} size={78} thickness={5} showLabel />
                </Stack>
            </Stack>

            <Box sx={{ mt: 3, p: 2, borderRadius: '16px', bgcolor: SURFACE_SOFT }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE }}>SPEAKING TASK</Typography>
                <Typography sx={{ mt: 0.65, fontFamily: INTER, fontSize: 13.5, lineHeight: 1.55, color: NAVY }}>{attempt.promptEnglish}</Typography>
            </Box>

            <Box sx={{ mt: 2, p: 2.25, borderRadius: '16px', border: `1px solid ${RING_TRACK}` }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: BLUE }}>AI FEEDBACK · {attempt.aiScore}</Typography>
                <Typography sx={{ mt: 0.65, fontFamily: INTER, fontSize: 13.5, lineHeight: 1.55, color: NAVY }}>{attempt.aiFeedback}</Typography>
            </Box>

            {attempt.review ? (
                <Box sx={{ mt: 2, p: 2.25, borderRadius: '16px', bgcolor: `${GREEN}0D`, border: `1px solid ${GREEN}2B` }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <RateReviewRoundedIcon sx={{ color: GREEN, fontSize: 20 }} />
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SUCCESS_TEXT }}>FROM YOUR TEACHER · {attempt.review.teacherScore}</Typography>
                    </Stack>
                    <Typography sx={{ mt: 0.75, fontFamily: INTER, fontSize: 13.5, lineHeight: 1.55, color: NAVY }}>{attempt.review.comment}</Typography>
                    <Typography sx={{ mt: 0.75, fontFamily: INTER, fontSize: 11.5, color: SLATE }}>Reviewed {attempt.review.reviewedOn}</Typography>
                </Box>
            ) : (
                <Box sx={{ mt: 2, p: 2.25, borderRadius: '16px', bgcolor: `${YELLOW}29` }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 13.5, color: NAVY }}>Your teacher has not reviewed this recording yet</Typography>
                    <Typography sx={{ mt: 0.4, fontFamily: INTER, fontSize: 12.5, color: SLATE }}>Your AI score and pronunciation details are available while you wait.</Typography>
                </Box>
            )}

            <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${RING_TRACK}` }}>
                <Typography sx={{ mb: 1.5, fontFamily: POPPINS, fontWeight: 600, fontSize: 15.5, color: NAVY }}>Pronunciation details</Typography>
                <PronunciationDetail grades={attempt.grades} />
            </Box>
        </Box>
    );
}

export default function StudentDesktopFeedbackPage({ showEmpty = false }: { showEmpty?: boolean }) {
    const attempts = showEmpty ? [] : mockFeedbackAttempts;
    const [filter, setFilter] = React.useState<FeedbackFilter>('all');
    const [selectedId, setSelectedId] = React.useState(attempts[0]?.id ?? '');
    const reviewedCount = attempts.filter(attempt => attempt.review != null).length;
    const awaitingCount = attempts.length - reviewedCount;
    const filtered = attempts.filter(attempt => filter === 'all' || (filter === 'reviewed' ? attempt.review != null : attempt.review == null));
    const selected = filtered.find(attempt => attempt.id === selectedId) ?? filtered[0];

    const changeFilter = (_event: React.SyntheticEvent, value: FeedbackFilter) => {
        setFilter(value);
        const first = attempts.find(attempt => value === 'all' || (value === 'reviewed' ? attempt.review != null : attempt.review == null));
        if (first) setSelectedId(first.id);
    };

    return (
        <Box sx={{ maxWidth: 1240, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-end' }} spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, md: 30 }, color: NAVY }}>Teacher feedback</Typography>
                    <Typography sx={{ mt: 0.4, fontFamily: INTER, fontSize: 14, color: SLATE }}>Review your speaking submissions, AI feedback, and teacher comments.</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Chip icon={<CheckCircleRoundedIcon />} label={`${reviewedCount} reviewed`} sx={{ bgcolor: `${GREEN}12`, color: SUCCESS_TEXT, fontFamily: INTER, fontWeight: 700, '& .MuiChip-icon': { color: GREEN } }} />
                    <Chip icon={<HourglassTopRoundedIcon />} label={`${awaitingCount} awaiting`} sx={{ bgcolor: `${YELLOW}32`, color: NAVY, fontFamily: INTER, fontWeight: 700, '& .MuiChip-icon': { color: NAVY } }} />
                </Stack>
            </Stack>

            {attempts.length === 0 ? (
                <Box sx={{ ...cardSx, py: 7, px: 3, textAlign: 'center' }}>
                    <RateReviewRoundedIcon sx={{ fontSize: 38, color: SLATE }} />
                    <Typography sx={{ mt: 1.5, fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>No speaking submissions yet</Typography>
                    <Typography sx={{ mt: 0.5, fontFamily: INTER, fontSize: 13.5, color: SLATE }}>Complete a speaking exercise and check back for feedback.</Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '340px minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
                    <Box>
                        <Tabs value={filter} onChange={changeFilter} variant="fullWidth" sx={{ mb: 1.5, minHeight: 42, bgcolor: '#fff', borderRadius: '14px', '& .MuiTabs-indicator': { bgcolor: BLUE }, '& .MuiTab-root': { minHeight: 42, fontFamily: INTER, fontWeight: 700, fontSize: 11.5, textTransform: 'none' } }}>
                            <Tab value="all" label="All" />
                            <Tab value="reviewed" label="Reviewed" />
                            <Tab value="awaiting" label="Awaiting" />
                        </Tabs>
                        <Stack spacing={1.25}>
                            {filtered.map(attempt => <AttemptListItem key={attempt.id} attempt={attempt} selected={attempt.id === selected?.id} onSelect={() => setSelectedId(attempt.id)} />)}
                        </Stack>
                    </Box>
                    {selected && <FeedbackDetail attempt={selected} />}
                </Box>
            )}
        </Box>
    );
}
